import {
  Paper,
  styled,
  Box,
  Grid,
  Typography,
  IconButton,
  Checkbox,
  Divider,
  MenuList,
  MenuItem,
  ListItemText,
} from "@mui/material";
import { IOpsLinkTopic, IFlight } from "@vmosolutions/oc-interfaces";
import { defaultTimezone } from "src/redux/reducers/CrewScheduleReducer";
import { formatDateString } from "src/components/DateDisplay";
import { useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { FlightChip } from "src/components/Flights/FlightChip";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import _ from "lodash";

interface IOpsLinkTopics {
  id: string;
  headerHeight: number;
  selectedFlights: IFlight[];
  selectedTopics: IOpsLinkTopic[];
  setSelectedTopics: React.Dispatch<React.SetStateAction<IOpsLinkTopic[]>>;
}

const OCOpsLinkTopics = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "headerHeight",
})((prop: { headerHeight: number }) => ({
  padding: "5px",
  height: "100%",
  width: "400px",
  minWidth: "300px",
  "& .flightchip": {
    margin: "0 auto 5px auto",
  },
  "& .topicVal": {
    textAlign: "end",
  },
  "& .selected": {
    backgroundColor: "##888888",
  },
}));

export default function OpsLinkTopics(props: IOpsLinkTopics): JSX.Element {
  const { id, headerHeight, selectedFlights, selectedTopics, setSelectedTopics } = props;
  const outerHeight = headerHeight + 225;
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const flightTypes = useSelector((state: IRootState) => state.structures.customerStructure.flightTypes);
  const topics = useSelector((state: IRootState) => state.OpsLinkReducer.topicList);

  const currentFlight = useMemo(() => {
    return selectedFlights[currentIndex];
  }, [selectedFlights, currentIndex]);

  const currentType = useMemo(() => {
    const type = flightTypes.find((type) => type._id === currentFlight?.flightType?._id);
    return type;
  }, [currentFlight]);

  const updateSelectedTopics = useCallback(
    (topic: IOpsLinkTopic) => {
      const tempTopics = _.cloneDeep(selectedTopics);
      const selectedIndex = tempTopics.findIndex((currTopic: IOpsLinkTopic) => topic._id === currTopic._id);
      if (selectedIndex < 0) {
        tempTopics.push(topic);
      } else {
        tempTopics.splice(selectedIndex, 1);
      }
      setSelectedTopics(tempTopics);
    },
    [selectedTopics]
  );

  const toggleAllTopics = useCallback(() => {
    if (selectedTopics.length === topics.length) {
      setSelectedTopics([]);
    } else {
      setSelectedTopics(topics);
    }
  }, [selectedTopics, topics]);

  return (
    <OCOpsLinkTopics elevation={1} headerHeight={headerHeight}>
      {selectedFlights?.length > 0 && (
        //header with selected flight(s) info
        <Box
          sx={{
            height: "auto",
            width: "auto",
            padding: "3px",
            display: "grid",
            justifyContent: "center",
          }}
        >
          <FlightChip data={currentFlight} />
          <Grid container sx={{ width: "100%", justifyContent: "center", textAlign: "justify", padding: "3px" }}>
            <Grid xs={6} item>
              <Typography variant="body2">Flight Type</Typography>
            </Grid>
            <Grid xs={6} item className="topicVal">
              <Typography variant="body1">{currentType?.description}</Typography>
            </Grid>

            <Grid xs={6} item>
              <Typography variant="body2">Departure</Typography>
            </Grid>
            <Grid xs={6} item className="topicVal">
              <Typography variant="body1">
                {formatDateString(currentFlight.actualOut, defaultTimezone.timezoneId)}
              </Typography>
            </Grid>

            <Grid xs={6} item>
              <Typography variant="body2">Arrival</Typography>
            </Grid>
            <Grid xs={6} item className="topicVal">
              <Typography variant="body1">
                {formatDateString(currentFlight.actualIn, defaultTimezone.timezoneId)}
              </Typography>
            </Grid>

            <Grid xs={6} item>
              <Typography variant="body2">Registration</Typography>
            </Grid>
            <Grid xs={6} item className="topicVal">
              <Typography variant="body1">{selectedFlights[currentIndex]?.aircraft?.registration ?? ""}</Typography>
            </Grid>
          </Grid>
          <Grid container sx={{ height: "50px", alignItems: "center", justifyContent: "center" }}>
            <Grid item>
              <IconButton disabled={currentIndex === 0} onClick={() => setCurrentIndex(currentIndex - 1)}>
                <ArrowBackIcon />
              </IconButton>
            </Grid>
            <Grid item>
              <Typography variant="body1">
                {currentIndex + 1} of {selectedFlights.length}
              </Typography>
            </Grid>
            <Grid item>
              <IconButton
                disabled={currentIndex === selectedFlights.length - 1}
                onClick={() => setCurrentIndex(currentIndex + 1)}
              >
                <ArrowForwardIcon />
              </IconButton>
            </Grid>
          </Grid>

          <Divider sx={{ borderColor: "#666666" }} />

          <Grid container sx={{ width: "100%", alignItems: "center", padding: "3px" }}>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                Select All
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "end" }}>
              <Checkbox
                checked={selectedTopics.length === topics.length}
                sx={{ padding: "9px 0" }}
                onChange={toggleAllTopics}
              />
            </Grid>
          </Grid>

          <MenuList sx={{ overflowY: "auto", maxHeight: `calc(95vh - ${outerHeight}px)` }}>
            {topics.map((topic, index) => {
              return (
                <MenuItem
                  onClick={() => updateSelectedTopics(topic)}
                  selected={!!selectedTopics.find((st) => st._id === topic._id)}
                  sx={{ cursor: "pointer" }}
                  key={topic._id}
                >
                  <ListItemText sx={{ paddingLeft: "4px" }} primary={`${topic.longName}`} />
                </MenuItem>
              );
            })}
          </MenuList>
        </Box>
      )}
    </OCOpsLinkTopics>
  );
}
